/**
 * Document Import Context
 *
 * Manages global state for active document imports.
 * Tracks processing status across all screens and provides:
 * - Loading bar visibility and progress
 * - Import button disabled state
 * - Notification banner on completion
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { useDocumentStatus } from '../services/api/queries/useDocumentImport';
import { AppState } from 'react-native';
import { DEBUG } from '../config/api';

export interface ActiveImport {
  importId: number;
  fileName?: string;
  fileType: 'image' | 'pdf';
  status: 'pending' | 'processing' | 'complete' | 'failed';
  startTime: number;
  errorMessage?: string; // Error message from backend when status is 'failed'
}

interface DocumentImportContextType {
  // Active import tracking
  activeImport: ActiveImport | null;

  // UI state
  showLoadingBar: boolean;
  processingProgress: number; // 0-100 (estimated)

  // Actions
  startImport: (importId: number, fileName?: string, fileType?: 'image' | 'pdf') => void;
  clearImport: () => void;
  updateImportStatus: (status: 'pending' | 'processing' | 'complete' | 'failed') => void;

  // Computed
  isImporting: boolean;
  canImport: boolean; // false if already importing
}

const DocumentImportContext = createContext<DocumentImportContextType | undefined>(undefined);

export function DocumentImportProvider({ children }: { children: ReactNode }) {
  const [activeImport, setActiveImport] = useState<ActiveImport | null>(null);
  const [processingProgress, setProcessingProgress] = useState(0);

  // Track if we've received complete status from server
  const [confirmedComplete, setConfirmedComplete] = useState(false);

  // Poll status when active import exists
  // Keep polling until we've confirmed 'complete' or 'failed' status from server
  // Poll for 'pending', 'processing', and 'complete' (until confirmed) statuses
  const pollingEnabled =
    !!activeImport &&
    !confirmedComplete &&
    (activeImport.status === 'pending' || activeImport.status === 'processing');
  if (DEBUG)
    console.log('[DocumentImportContext] State:', {
      hasActiveImport: !!activeImport,
      importId: activeImport?.importId,
      status: activeImport?.status,
      confirmedComplete,
      pollingEnabled,
    });
  const { data: statusData, refetch } = useDocumentStatus(
    activeImport?.importId || 0,
    pollingEnabled
  );

  // Update status when polling returns new data
  useEffect(() => {
    if (DEBUG)
      console.log(
        '[DocumentImportContext] Status effect - activeImport.status:',
        activeImport?.status,
        'statusData.status:',
        statusData?.status
      );
    if (activeImport && statusData && statusData.status !== activeImport.status) {
      const newStatus = statusData.status as ActiveImport['status'];
      if (DEBUG)
        console.log(
          '[DocumentImportContext] *** Status changing from',
          activeImport.status,
          'to',
          newStatus,
          '***'
        );

      // Update status and capture error message if failed
      if (newStatus === 'failed' && statusData.error_message) {
        setActiveImport((prev) =>
          prev ? { ...prev, status: newStatus, errorMessage: statusData.error_message } : null
        );
      } else {
        setActiveImport((prev) => (prev ? { ...prev, status: newStatus } : null));
      }

      // Set progress to 100% when complete and mark as confirmed
      if (newStatus === 'complete') {
        if (DEBUG)
          console.log('[DocumentImportContext] *** Marking as COMPLETE, stopping polling ***');
        setProcessingProgress(100);
        setConfirmedComplete(true);
      }

      // Mark as failed if needed
      if (newStatus === 'failed') {
        if (DEBUG) console.log('[DocumentImportContext] *** Marking as FAILED ***');
        setConfirmedComplete(true);

        // Log the error for debugging
        if (statusData.error_message) {
          console.error('[DocumentImportContext] Import failed:', statusData.error_message);
        }

        // Auto-clear failed import after 5 seconds so user can try again
        const timer = setTimeout(() => {
          if (DEBUG) console.log('[DocumentImportContext] *** Auto-clearing failed import ***');
          setActiveImport(null);
          setProcessingProgress(0);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [statusData, activeImport]);

  // Simulate progress during processing
  useEffect(() => {
    if (activeImport?.status === 'processing') {
      const interval = setInterval(() => {
        setProcessingProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 2000); // Increment every 2 seconds
      return () => clearInterval(interval);
    } else if (activeImport?.status === 'pending') {
      setProcessingProgress(0);
    }
  }, [activeImport?.status]);

  // Handle app state changes (background/foreground)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'active' && activeImport) {
        // Refetch status when app comes to foreground
        refetch();
      }
    });
    return () => subscription.remove();
  }, [activeImport, refetch]);

  const startImport = useCallback(
    (importId: number, fileName?: string, fileType?: 'image' | 'pdf') => {
      if (DEBUG)
        console.log('[DocumentImportContext] Starting import:', { importId, fileName, fileType });
      setConfirmedComplete(false); // Reset completion flag
      setActiveImport({
        importId,
        fileName,
        fileType: fileType || 'image',
        status: 'pending',
        startTime: Date.now(),
      });
      setProcessingProgress(0);
    },
    []
  );

  const clearImport = useCallback(() => {
    setConfirmedComplete(false);
    setActiveImport(null);
    setProcessingProgress(0);
  }, []);

  const updateImportStatus = useCallback(
    (status: 'pending' | 'processing' | 'complete' | 'failed') => {
      if (activeImport) {
        setActiveImport({ ...activeImport, status });
      }
    },
    [activeImport]
  );

  const value: DocumentImportContextType = {
    activeImport,
    showLoadingBar: !!activeImport && activeImport.status !== 'failed',
    processingProgress,
    startImport,
    clearImport,
    updateImportStatus,
    isImporting: !!activeImport && activeImport.status !== 'failed',
    canImport: !activeImport || activeImport.status === 'failed', // Allow import when no active import OR when previous import failed
  };

  return <DocumentImportContext.Provider value={value}>{children}</DocumentImportContext.Provider>;
}

export function useDocumentImportContext() {
  const context = useContext(DocumentImportContext);
  if (!context) {
    throw new Error('useDocumentImportContext must be used within DocumentImportProvider');
  }
  return context;
}
